<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useContactLabelsStore } from 'dashboard/stores/contactLabels';
import { useDealsStore } from 'dashboard/stores/deals';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import ContactAPI from 'dashboard/api/contacts';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import ContactDeleteSection from 'dashboard/components-next/Contacts/ContactsCard/ContactDeleteSection.vue';
import ContactCompanySection from 'dashboard/components-next/Contacts/ContactsSidebar/ContactCompanySection.vue';
import ContactHistory from 'dashboard/components-next/Contacts/ContactsSidebar/ContactHistory.vue';
import DealInfo from 'dashboard/routes/dashboard/conversation/contact/DealInfo.vue';
import LinkedTasks from 'dashboard/components-next/Tasks/LinkedTasks.vue';

const props = defineProps({
  contact: { type: Object, required: true },
});

const emit = defineEmits(['back', 'updated']);

const { t } = useI18n();
const store = useStore();
const dealsStore = useDealsStore();
const contactLabelsStore = useContactLabelsStore();

const contactsFormRef = ref(null);
const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);
const isUpdating = computed(
  () => store.getters['contacts/getUIFlags'].isUpdating
);

const contactData = ref({
  id: props.contact.id,
  name: props.contact.name,
  email: props.contact.email,
  phoneNumber: props.contact.phoneNumber,
  companyId: props.contact.company?.id || null,
  additionalAttributes: props.contact.additionalAttributes,
});

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const handleUpdateContact = async () => {
  try {
    await store.dispatch('contacts/update', contactData.value);
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
    emit('updated');
  } catch (error) {
    const i18nPrefix = 'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM';
    if (error instanceof DuplicateContactException) {
      if (error.data.includes('email')) {
        useAlert(t(`${i18nPrefix}.EMAIL_ADDRESS.DUPLICATE`));
      } else if (error.data.includes('phone_number')) {
        useAlert(t(`${i18nPrefix}.PHONE_NUMBER.DUPLICATE`));
      }
    } else if (error instanceof ExceptionWithMessage) {
      useAlert(error.data);
    } else {
      useAlert(t(`${i18nPrefix}.ERROR_MESSAGE`));
    }
  }
};

// --- Tags (read-only, mesmo dado exibido hoje na lista) ---
const contactLabels = ref([]);
const labelColorMap = computed(() => contactLabelsStore.getLabelColorMap);

const fetchLabels = async () => {
  try {
    const { data } = await ContactAPI.getContactLabels(props.contact.id);
    contactLabels.value = data.payload || [];
  } catch {
    contactLabels.value = [];
  }
};

// --- Tabs: Detalhes / Empresas / Negociações / Tarefas / Conversas ---
const TABS_OPTIONS = [
  { key: 'DETAILS', value: 'details' },
  { key: 'COMPANY', value: 'company' },
  { key: 'DEALS', value: 'deals' },
  { key: 'TASKS', value: 'tasks' },
  { key: 'CONVERSATIONS', value: 'conversations' },
];

const activeTab = ref('details');
const dealsCount = ref(0);
const tasksCount = ref(0);

const fetchDealsCount = async () => {
  try {
    const deals = await dealsStore.getByContact(props.contact.id);
    dealsCount.value = deals.length;
  } catch {
    dealsCount.value = 0;
  }
};

const contactConversationsByContactId = useMapGetter(
  'contactConversations/getAllConversationsByContactId'
);
const conversationsCount = computed(
  () => contactConversationsByContactId.value(props.contact.id)?.length || 0
);
const fetchConversationsCount = () => {
  store.dispatch('contactConversations/get', props.contact.id);
};

const tabs = computed(() =>
  TABS_OPTIONS.map(tab => {
    if (tab.value === 'company') {
      return {
        label: t('CONTACT_DETAIL.TABS.COMPANY'),
        count: props.contact.companies?.length || undefined,
        value: tab.value,
      };
    }
    if (tab.value === 'deals') {
      return {
        label: t('CONTACT_DETAIL.TABS.DEALS'),
        count: dealsCount.value > 0 ? dealsCount.value : undefined,
        value: tab.value,
      };
    }
    if (tab.value === 'tasks') {
      return {
        label: t('TASKS.HEADER'),
        count: tasksCount.value > 0 ? tasksCount.value : undefined,
        value: tab.value,
      };
    }
    if (tab.value === 'conversations') {
      return {
        label: t('CONTACT_DETAIL.TABS.CONVERSATIONS'),
        count:
          conversationsCount.value > 0 ? conversationsCount.value : undefined,
        value: tab.value,
      };
    }
    return { label: t(`CONTACT_DETAIL.TABS.${tab.key}`), value: tab.value };
  })
);

const activeTabIndex = computed(() =>
  TABS_OPTIONS.findIndex(tab => tab.value === activeTab.value)
);

const handleTabChange = tab => {
  activeTab.value = tab.value;
};

onMounted(() => {
  contactLabelsStore.fetchAll();
  fetchLabels();
  fetchDealsCount();
  fetchConversationsCount();
});
</script>

<template>
  <div
    class="thin-scrollbar flex flex-col gap-4 w-full h-full overflow-y-auto border-r border-n-weak bg-n-solid-1 p-4"
  >
    <div class="flex items-center gap-2">
      <Button
        icon="i-lucide-arrow-left"
        variant="ghost"
        color="slate"
        size="xs"
        @click="emit('back')"
      />
      <Avatar
        :name="contact.name"
        :src="contact.thumbnail"
        :size="40"
        :status="contact.availabilityStatus"
        hide-offline-status
        rounded-full
      />
      <div class="flex flex-col min-w-0">
        <span
          class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12"
        >
          <span class="truncate">{{ contact.name }}</span>
          <span
            class="i-lucide-contact size-4 shrink-0 text-n-slate-11"
            aria-hidden="true"
          />
        </span>
        <span
          v-if="contact.identifier"
          class="text-xs text-n-slate-11 truncate"
        >
          #{{ contact.identifier }}
        </span>
      </div>
    </div>

    <div v-if="contactLabels.length" class="flex flex-wrap gap-1">
      <span
        v-for="label in contactLabels"
        :key="label.title"
        class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium text-white"
        :style="{ backgroundColor: labelColorMap[label.title] || '#6B7280' }"
      >
        {{ label.title }}
      </span>
    </div>

    <TabBar
      :tabs="tabs"
      :initial-active-tab="activeTabIndex"
      class="w-full [&>button]:w-full bg-n-alpha-black2"
      @tab-changed="handleTabChange"
    />

    <div v-if="activeTab === 'details'" class="flex flex-col gap-4">
      <ContactsForm
        ref="contactsFormRef"
        :contact-data="contactData"
        @update="handleFormUpdate"
      />
      <Button
        :label="t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')"
        size="sm"
        :is-loading="isUpdating"
        :disabled="isUpdating || isFormInvalid"
        @click="handleUpdateContact"
      />
      <ContactDeleteSection
        :selected-contact="{ id: contact.id, name: contact.name }"
      />
    </div>
    <ContactCompanySection
      v-if="activeTab === 'company'"
      :contact-id="contact.id"
      :companies="contact.companies"
      @company-updated="emit('updated')"
    />
    <DealInfo
      v-if="activeTab === 'deals'"
      highlight-rows
      :contact-id="contact.id"
      @deals-changed="count => (dealsCount = count)"
    />
    <LinkedTasks
      v-show="activeTab === 'tasks'"
      :contact-id="contact.id"
      @loaded="count => (tasksCount = count)"
    />
    <ContactHistory
      v-if="activeTab === 'conversations'"
      :contact-id="contact.id"
    />
  </div>
</template>
